"use client";
import { useDynamicConfig, useExperiment } from "@statsig/react-bindings";
import { useMemo } from "react";
import {
  StatsigLayerEnum,
  StatsigLayerParametersEnum,
  statsigConfigDefaultValues,
} from "@/constants/constants";

// Statsig experiment configuration: Maps experiment names to their parameters
export const statsigExperimentConfig: Partial<
  Record<StatsigLayerEnum, StatsigLayerParametersEnum[]>
> = {
  [StatsigLayerEnum.TRIAL_PLAN]: [StatsigLayerParametersEnum.PLAN_ID],
  [StatsigLayerEnum.CROSS_ICON_PAYWALL]: [
    StatsigLayerParametersEnum.SHOW_CROSS_ICON,
  ],
  [StatsigLayerEnum.TRUECALLER]: [
    StatsigLayerParametersEnum.SHOW_TC_BY_DEFAULT,
  ],
  [StatsigLayerEnum.PAYMENT_FAILED_FLOW_EXPERIMENT]: [
    StatsigLayerParametersEnum.SHOW_PAYMENT_FAIL_PAGE,
  ],
  [StatsigLayerEnum.PAYMENT_SUCCESS_PAGE]: [
    StatsigLayerParametersEnum.SHOW_NEW_PAYMENT_SUCCESS_PAGE,
  ],
  [StatsigLayerEnum.SCARCITY_TIMER]: [StatsigLayerParametersEnum.ENABLE_TIMER],
  [StatsigLayerEnum.CROSS_ICON_PAYMENT_SUCCESS]: [
    StatsigLayerParametersEnum.SHOW_CROSS_ICON_PAYMENT_SUCCESS,
  ],
  [StatsigLayerEnum.HYBRID_PAYWALL]: [
    StatsigLayerParametersEnum.HYBRID_PAYWALL_VARIANT,
  ],
  [StatsigLayerEnum.PAYMENT_SUCCESS_APP_REDIRECTION]: [
    StatsigLayerParametersEnum.REDIRECTION_TO_APP,
  ],
};

/**
 * Custom hook to fetch all Statsig experiment values dynamically
 * Uses the statsigExperimentConfig to determine which experiments and parameters to fetch
 */
export const useStatsigEventExperiments = () => {
  // Get all experiment instances (must be at top level, cannot be in a loop)
  const experiments = [
    {
      name: StatsigLayerEnum.TRIAL_PLAN,
      instance: useExperiment(StatsigLayerEnum.TRIAL_PLAN),
    },
    {
      name: StatsigLayerEnum.CROSS_ICON_PAYWALL,
      instance: useExperiment(StatsigLayerEnum.CROSS_ICON_PAYWALL),
    },
    {
      name: StatsigLayerEnum.TRUECALLER,
      instance: useExperiment(StatsigLayerEnum.TRUECALLER),
    },
    {
      name: StatsigLayerEnum.PAYMENT_FAILED_FLOW_EXPERIMENT,
      instance: useExperiment(StatsigLayerEnum.PAYMENT_FAILED_FLOW_EXPERIMENT),
    },
    {
      name: StatsigLayerEnum.PAYMENT_SUCCESS_PAGE,
      instance: useDynamicConfig(StatsigLayerEnum.PAYMENT_SUCCESS_PAGE),
    },
    {
      name: StatsigLayerEnum.SCARCITY_TIMER,
      instance: useExperiment(StatsigLayerEnum.SCARCITY_TIMER),
    },
    {
      name: StatsigLayerEnum.CROSS_ICON_PAYMENT_SUCCESS,
      instance: useExperiment(StatsigLayerEnum.CROSS_ICON_PAYMENT_SUCCESS),
    },
    {
      name: StatsigLayerEnum.HYBRID_PAYWALL,
      instance: useExperiment(StatsigLayerEnum.HYBRID_PAYWALL),
    },
    {
      name: StatsigLayerEnum.PAYMENT_SUCCESS_APP_REDIRECTION,
      instance: useExperiment(StatsigLayerEnum.PAYMENT_SUCCESS_APP_REDIRECTION),
    },
  ];

  // Build a stable dependency key from raw experiment values
  const depsKey = JSON.stringify(
    experiments.map(({ name, instance }) => {
      const parameters = statsigExperimentConfig[name];
      if (!parameters) return null;
      return parameters.map((p) =>
        instance.get(p, statsigConfigDefaultValues[p]),
      );
    }),
  );

  const experimentValues = useMemo(() => {
    const values: Record<
      string,
      Record<string, string | number | boolean>
    > = {};

    experiments.forEach(({ name, instance }) => {
      const parameters = statsigExperimentConfig[name];
      if (!parameters) return;

      values[name] = {};

      parameters.forEach((parameter) => {
        const value = instance.get(
          parameter,
          statsigConfigDefaultValues[parameter],
        );
        values[name][parameter] = value;
      });
    });

    return values;
    // depsKey is a JSON.stringify of all experiment values — it captures the actual
    // data that experiments array provides, avoiding a new object ref each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  return experimentValues;
};
